/**
 * Outil « Couleurs et absorbance » (Boîte à outils, physique-chimie 1re et Tle).
 *
 * Explorer :
 * - Additive (1re) : trois projecteurs R, V, B sur un écran noir (mélange par « screen ») ; les
 *   trois sortes de cônes stimulées.
 * - Soustractive (1re) : lampe, filtres, objet coloré, œil ; les rayons absorbés s'arrêtent.
 * - Absorbance (1re, Tle) : spectre d'absorption d'une solution, couleur de la cuve, droite
 *   d'étalonnage A = k × c et dosage d'une solution inconnue.
 * Défi : une question par sorte selon le niveau. Logique : `colors.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  COMPS,
  COMP_NAME,
  FILTERS,
  OBJECTS,
  SOLUTIONS,
  absText,
  absorbance,
  absorbanceLines,
  additiveLines,
  colorName,
  compsText,
  concText,
  epsAt,
  fr,
  hexOf,
  makeQuiz,
  nameOf,
  partsFor,
  perceived,
  quizKinds,
  rgbCss,
  sci,
  set3,
  solutionColor,
  subtractiveLines,
  unknownC,
  wavelengthName,
  wavelengthRgb,
  type Comp,
  type Part,
} from './colors';

const W = 340;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-500)';
const HOT = 'var(--color-error-500)';
const PURE: Record<Comp, string> = { R: '#ef4444', V: '#22c55e', B: '#3b82f6' };

function Lines({ lines }: { lines: string[] }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid="co-law">
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string, string?]> }) {
  return (
    <table className="w-full text-sm" data-testid="co-readings">
      <tbody>
        {rows.map(([k, v, swatch]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              <span className="inline-flex items-center gap-2">
                {swatch && (
                  <span
                    className="border-ink-300 inline-block h-4 w-4 shrink-0 rounded-full border"
                    style={{ background: swatch }}
                    aria-hidden
                  />
                )}
                {frenchSpaces(v)}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- additive ---------- */

function AdditiveFigure({ r, g, b }: { r: number; g: number; b: number }) {
  const spots: Array<[number, number, string]> = [
    [134, 82, rgbCss(r, 0, 0)],
    [206, 82, rgbCss(0, g, 0)],
    [170, 142, rgbCss(0, 0, b)],
  ];
  const cones: Array<[string, number, string]> = [
    ['cônes « rouge »', r, PURE.R],
    ['cônes « vert »', g, PURE.V],
    ['cônes « bleu »', b, PURE.B],
  ];
  return (
    <svg
      viewBox={`0 0 ${W} 282`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Projecteurs rouge ${fr(100 * r, 0)} %, vert ${fr(100 * g, 0)} %, bleu ${fr(100 * b, 0)} % : au centre, ${colorName(r, g, b)}`}
      data-testid="co-additive"
    >
      <rect x={14} y={10} width={W - 28} height={200} rx={10} fill="#000" />
      <g style={{ isolation: 'isolate' }}>
        {spots.map(([x, y, c], i) => (
          <circle key={i} cx={x} cy={y} r={62} fill={c} style={{ mixBlendMode: 'screen' }} />
        ))}
      </g>
      <text x={24} y={28} fontSize={10} fill="#d4d4d8">
        écran noir
      </text>
      {cones.map(([label, v, c], i) => (
        <g key={label}>
          <text x={14} y={234 + i * 16} fontSize={10} fill={MUTED}>
            {label}
          </text>
          <rect
            x={118}
            y={226 + i * 16}
            width={W - 132}
            height={9}
            rx={4}
            fill="var(--color-ink-100)"
          />
          <rect x={118} y={226 + i * 16} width={(W - 132) * v} height={9} rx={4} fill={c} />
        </g>
      ))}
    </svg>
  );
}

function AdditiveView() {
  const [r, setR] = useState(1);
  const [g, setG] = useState(1);
  const [b, setB] = useState(0);
  const pct = (v: number) => frenchSpaces(`${fr(100 * v, 0)} %`);
  return (
    <div className="space-y-4">
      <AdditiveFigure r={r} g={g} b={b} />
      <ParamSlider
        name="Projecteur rouge"
        ariaLabel="Rouge"
        value={r}
        min={0}
        max={1}
        step={0.05}
        onChange={setR}
        format={pct}
        color={PURE.R}
      />
      <ParamSlider
        name="Projecteur vert"
        ariaLabel="Vert"
        value={g}
        min={0}
        max={1}
        step={0.05}
        onChange={setG}
        format={pct}
        color={PURE.V}
      />
      <ParamSlider
        name="Projecteur bleu"
        ariaLabel="Bleu"
        value={b}
        min={0}
        max={1}
        step={0.05}
        onChange={setB}
        format={pct}
        color={PURE.B}
      />
      <Readout rows={[['Au centre (les trois mélangées)', colorName(r, g, b), rgbCss(r, g, b)]]} />
      <Lines lines={additiveLines(r, g, b)} />
    </div>
  );
}

/* ---------- soustractive ---------- */

function SubtractiveFigure({
  light,
  f1,
  f2,
  objIdx,
}: {
  light: ReturnType<typeof set3>;
  f1: number;
  f2: number;
  objIdx: number;
}) {
  const filters = [FILTERS[f1]!, FILTERS[f2]!];
  const obj = OBJECTS[objIdx]!;
  const { seen } = perceived({ light, filters, object: obj });
  const lampX = 34;
  const xs = [92, 158];
  const objX = 222;
  const ys: Record<Comp, number> = { R: 66, V: 78, B: 90 };
  const eye = { x: 304, y: 168 };
  // jusqu'où va chaque composante
  const stop = (c: Comp) => {
    if (!light[c]) return null;
    for (let i = 0; i < 2; i++)
      if (!filters[i]!.keeps[c] && filters[i]!.id !== 'none') return xs[i]!;
    return objX - 26;
  };
  return (
    <svg
      viewBox={`0 0 ${W} 214`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Lampe ${nameOf(light)}, objet ${obj.label} : il paraît ${nameOf(seen)}`}
      data-testid="co-subtractive"
    >
      {/* lampe */}
      <circle
        cx={lampX}
        cy={78}
        r={16}
        fill={nameOf(light) === 'noir' ? '#3f3f46' : hexOf(light)}
        stroke={INK}
      />
      <rect x={lampX - 6} y={94} width={12} height={10} fill="var(--color-ink-400)" stroke={INK} />
      <text x={lampX} y={122} textAnchor="middle" fontSize={10} fill={MUTED}>
        lampe
      </text>
      {COMPS.map((c) => {
        const end = stop(c);
        if (end === null) return null;
        return (
          <g key={c} data-testid={`co-ray-${c}`}>
            <line x1={lampX + 16} y1={ys[c]} x2={end} y2={ys[c]} stroke={PURE[c]} strokeWidth={3} />
            {end !== objX - 26 && (
              <text x={end + 4} y={ys[c] + 4} fontSize={10} fill={PURE[c]} fontWeight={700}>
                ×
              </text>
            )}
          </g>
        );
      })}
      {filters.map((f, i) =>
        f.id === 'none' ? null : (
          <g key={i} data-testid="co-filter">
            <rect
              x={xs[i]! - 4}
              y={50}
              width={8}
              height={56}
              rx={2}
              fill={hexOf(f.keeps)}
              opacity={0.75}
              stroke={INK}
            />
            <text x={xs[i]!} y={122} textAnchor="middle" fontSize={10} fill={MUTED}>
              {f.label}
            </text>
          </g>
        ),
      )}
      {/* objet */}
      <circle
        cx={objX}
        cy={78}
        r={26}
        fill={hexOf(seen)}
        stroke={INK}
        strokeWidth={1.5}
        data-testid="co-object"
      />
      <text x={objX} y={122} textAnchor="middle" fontSize={10} fill={MUTED}>
        {obj.label.replace(/ \(.*\)/, '').toLowerCase()}
      </text>
      {/* œil */}
      <ellipse cx={eye.x} cy={eye.y} rx={16} ry={9} fill="white" stroke={INK} />
      <circle cx={eye.x - 4} cy={eye.y} r={4.5} fill={INK} />
      <text x={eye.x} y={eye.y + 24} textAnchor="middle" fontSize={10} fill={MUTED}>
        œil
      </text>
      {COMPS.filter((c) => seen[c]).map((c, i) => (
        <line
          key={c}
          x1={objX + 18}
          y1={96 + i * 4}
          x2={eye.x - 18}
          y2={eye.y - 6 + i * 4}
          stroke={PURE[c]}
          strokeWidth={2.5}
        />
      ))}
      <HaloText x={objX} y={40} textAnchor="middle" fontSize={11} fontWeight={700} fill={INK}>
        {`paraît ${nameOf(seen)}`}
      </HaloText>
    </svg>
  );
}

function SubtractiveView() {
  const [light, setLight] = useState(set3(true, true, true));
  const [f1, setF1] = useState(0);
  const [f2, setF2] = useState(0);
  const [objIdx, setObjIdx] = useState(1);
  const filters = [FILTERS[f1]!, FILTERS[f2]!];
  const obj = OBJECTS[objIdx]!;
  const { afterFilters, seen } = perceived({ light, filters, object: obj });
  const toggle = (c: Comp) => setLight({ ...light, [c]: !light[c] });
  return (
    <div className="space-y-4">
      <SubtractiveFigure light={light} f1={f1} f2={f2} objIdx={objIdx} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-ink-600 text-sm font-semibold">Lampe :</span>
        {COMPS.map((c) => (
          <ToggleChip key={c} on={light[c]} onClick={() => toggle(c)}>
            {COMP_NAME[c][0]!.toUpperCase() + COMP_NAME[c].slice(1)}
          </ToggleChip>
        ))}
      </div>
      <Stepper
        label="Filtre 1"
        value={f1}
        min={0}
        max={FILTERS.length - 1}
        onChange={setF1}
        format={(i) => FILTERS[i]!.label}
      />
      <Stepper
        label="Filtre 2"
        value={f2}
        min={0}
        max={FILTERS.length - 1}
        onChange={setF2}
        format={(i) => FILTERS[i]!.label}
      />
      <Stepper
        label="Objet"
        value={objIdx}
        min={0}
        max={OBJECTS.length - 1}
        onChange={setObjIdx}
        format={(i) => OBJECTS[i]!.label}
      />
      <Readout
        rows={[
          ['Lumière de la lampe', `${nameOf(light)} (${compsText(light)})`, hexOf(light)],
          ['Après les filtres', nameOf(afterFilters), hexOf(afterFilters)],
          ['L’objet diffuse', compsText(obj.keeps)],
          ['Couleur perçue', nameOf(seen), hexOf(seen)],
        ]}
      />
      <Lines lines={subtractiveLines({ light, filters, object: obj })} />
    </div>
  );
}

/* ---------- absorbance ---------- */

const L0 = 400;
const L1 = 800;

function AbsorbanceFigure({
  solId,
  c,
  lambda,
  unknown,
}: {
  solId: string;
  c: number;
  lambda: number;
  unknown: boolean;
}) {
  const s = SOLUTIONS.find((x) => x.id === solId)!;
  // spectre
  const L = 40;
  const Rr = W - 14;
  const T = 22;
  const B = 140;
  const aMax = Math.max(0.2, absorbance(s, s.lmax, s.cmax)) * 1.05;
  const X = (l: number) => L + ((l - L0) / (L1 - L0)) * (Rr - L);
  const Y = (a: number) => B - (Math.min(a, aMax) / aMax) * (B - T);
  const pts = Array.from({ length: 81 }, (_, k) => L0 + k * 5).map(
    (l) => `${X(l)},${Y(absorbance(s, l, c))}`,
  );
  const A = absorbance(s, lambda, c);
  const [lr, lg, lb] = wavelengthRgb(lambda);
  const beam = rgbCss(lr, lg, lb);
  const [sr, sg, sb] = solutionColor(s, c);
  // étalonnage
  const gL = 136;
  const gR = W - 14;
  const gT = 196;
  const gB = 300;
  const aCal = absorbance(s, lambda, s.cmax) * 1.05 || 1;
  const GX = (cc: number) => gL + (cc / s.cmax) * (gR - gL);
  const GY = (a: number) => gB - (a / aCal) * (gB - gT);
  const cx = unknownC(s);
  const Ax = absorbance(s, lambda, cx);
  return (
    <svg
      viewBox={`0 0 ${W} 326`}
      className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
      role="img"
      aria-label={`Spectre d’absorption, maximum vers ${s.lmax} nm ; à ${lambda} nm, A = ${absText(A)}`}
      data-testid="co-absorbance"
    >
      <defs>
        <linearGradient id="co-rainbow" x1="0" x2="1" y1="0" y2="0">
          {Array.from({ length: 21 }, (_, k) => {
            const l = L0 + k * 20;
            const [r, g, b] = wavelengthRgb(l);
            return <stop key={k} offset={k / 20} stopColor={rgbCss(r, g, b)} />;
          })}
        </linearGradient>
      </defs>
      <rect x={L} y={B + 4} width={Rr - L} height={8} fill="url(#co-rainbow)" />
      <line x1={L} y1={B} x2={Rr} y2={B} stroke="var(--color-ink-300)" />
      <line x1={L} y1={T} x2={L} y2={B} stroke="var(--color-ink-300)" />
      <text x={L + 4} y={T - 8} fontSize={10} fontWeight={700} fill={INK}>
        A
      </text>
      {[400, 500, 600, 700, 800].map((l) => (
        <text key={l} x={X(l)} y={B + 24} textAnchor="middle" fontSize={9.5} fill={MUTED}>
          {l}
        </text>
      ))}
      <text x={Rr} y={T - 8} textAnchor="end" fontSize={10} fill={MUTED}>
        λ (nm)
      </text>
      <polyline points={pts.join(' ')} fill="none" stroke={INK} strokeWidth={2.2} />
      <line
        x1={X(lambda)}
        y1={T}
        x2={X(lambda)}
        y2={B}
        stroke={beam}
        strokeWidth={2}
        strokeDasharray="4 3"
      />
      <circle cx={X(lambda)} cy={Y(A)} r={5} fill={HOT} stroke="white" strokeWidth={1.5} />
      <text x={L - 4} y={T + 4} textAnchor="end" fontSize={9.5} fill={MUTED}>
        {absText(aMax)}
      </text>
      <text x={L - 4} y={B} textAnchor="end" fontSize={9.5} fill={MUTED}>
        0
      </text>
      {/* cuve */}
      <line x1={8} y1={248} x2={42} y2={248} stroke={beam} strokeWidth={4} />
      <rect
        x={42}
        y={206}
        width={44}
        height={84}
        rx={3}
        fill={rgbCss(sr, sg, sb)}
        stroke={INK}
        strokeWidth={1.5}
        data-testid="co-cuvette"
      />
      <line
        x1={86}
        y1={248}
        x2={122}
        y2={248}
        stroke={beam}
        strokeWidth={4}
        opacity={Math.max(0.08, 10 ** -A)}
      />
      <text x={64} y={306} textAnchor="middle" fontSize={10} fill={MUTED}>
        ℓ = 1 cm
      </text>
      <text x={64} y={198} textAnchor="middle" fontSize={10} fill={MUTED}>
        {s.formula}
      </text>
      {/* étalonnage */}
      <line x1={gL} y1={gB} x2={gR} y2={gB} stroke="var(--color-ink-300)" />
      <line x1={gL} y1={gT} x2={gL} y2={gB} stroke="var(--color-ink-300)" />
      <text x={gL + 4} y={gT - 6} fontSize={10} fontWeight={700} fill={INK}>
        {`A à ${lambda} nm`}
      </text>
      <text x={gR} y={gB + 14} textAnchor="end" fontSize={10} fill={MUTED}>
        c →
      </text>
      <line
        x1={GX(0)}
        y1={GY(0)}
        x2={GX(s.cmax)}
        y2={GY(absorbance(s, lambda, s.cmax))}
        stroke={INK}
        strokeWidth={2}
      />
      {unknown && (
        <g data-testid="co-unknown">
          <line x1={gL} y1={GY(Ax)} x2={GX(cx)} y2={GY(Ax)} stroke={HOT} strokeDasharray="4 3" />
          <line x1={GX(cx)} y1={GY(Ax)} x2={GX(cx)} y2={gB} stroke={HOT} strokeDasharray="4 3" />
          <HaloText x={GX(cx) + 4} y={gB - 4} fontSize={10} fill={HOT} fontWeight={700}>
            c inconnue
          </HaloText>
        </g>
      )}
      <circle cx={GX(c)} cy={GY(A)} r={5} fill={HOT} stroke="white" strokeWidth={1.5} />
    </svg>
  );
}

function AbsorbanceView({ rank }: { rank: number | null }) {
  const [solId, setSolId] = useState(SOLUTIONS[0]!.id);
  const s = SOLUTIONS.find((x) => x.id === solId)!;
  const [frac, setFrac] = useState(0.4);
  const [lambda, setLambda] = useState(s.lmax);
  const [unknown, setUnknown] = useState(false);
  const c = frac * s.cmax;
  const A = absorbance(s, lambda, c);
  const [sr, sg, sb] = solutionColor(s, c);
  const [lr, lg, lb] = wavelengthRgb(lambda);
  const rows: Array<[string, string, string?]> = [
    ['Longueur d’onde λ', `${lambda} nm (${wavelengthName(lambda)})`, rgbCss(lr, lg, lb)],
    ['ε à cette longueur d’onde', `${fr(epsAt(s, lambda), 0)} L·mol⁻¹·cm⁻¹`],
    ['Concentration c', `${concText(s, c)} = ${sci(c)} mol/L`],
    ['Absorbance A = ε × ℓ × c', absText(A)],
    ['Lumière transmise', `${fr(100 * 10 ** -A, 0)} %`],
    ['Couleur de la solution', colorName(sr, sg, sb), rgbCss(sr, sg, sb)],
  ];
  if (unknown) {
    const cx = unknownC(s);
    rows.push(['Solution inconnue : A mesurée', absText(absorbance(s, lambda, cx))]);
    rows.push(['c = A ÷ (ε × ℓ)', concText(s, cx)]);
  }
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Solution"
        value={solId}
        options={SOLUTIONS.map((x) => ({ value: x.id, label: x.label }))}
        onChange={(v) => {
          setSolId(v);
          setLambda(Math.min(L1, SOLUTIONS.find((x) => x.id === v)!.lmax));
        }}
      />
      <AbsorbanceFigure solId={solId} c={c} lambda={lambda} unknown={unknown} />
      <ParamSlider
        name="Concentration c"
        ariaLabel="Concentration"
        value={frac}
        min={0}
        max={1}
        step={0.02}
        onChange={setFrac}
        format={() => frenchSpaces(concText(s, c))}
      />
      <ParamSlider
        name="Longueur d’onde du spectrophotomètre"
        ariaLabel="Longueur d’onde"
        value={lambda}
        min={L0}
        max={L1}
        step={5}
        onChange={setLambda}
        format={(v) => frenchSpaces(`${v} nm`)}
        color={rgbCss(lr, lg, lb)}
      />
      <ToggleChip on={unknown} onClick={() => setUnknown(!unknown)}>
        Doser une solution inconnue
      </ToggleChip>
      <Readout rows={rows} />
      <Lines lines={absorbanceLines(s, lambda, c, rank)} />
    </div>
  );
}

function Explore({ rank }: { rank: number | null }) {
  const parts = partsFor(rank);
  const [part, setPart] = useState<Part>(parts[0]!.id);
  return (
    <div className="space-y-4">
      {parts.length > 1 && (
        <Segmented
          ariaLabel="Partie"
          value={part}
          options={parts.map((p) => ({ value: p.id, label: p.label }))}
          onChange={setPart}
        />
      )}
      {part === 'additive' && <AdditiveView />}
      {part === 'subtractive' && <SubtractiveView />}
      {part === 'absorbance' && <AbsorbanceView rank={rank} />}
    </div>
  );
}

export function ColorsTool({
  level = null,
  seed,
}: {
  /** niveau : 1re (synthèses, absorbance) ou Tle (absorbance, Beer-Lambert) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="co" rich />
      </div>
    </div>
  );
}
